import React, { useState, useCallback } from 'react'

import Modal from 'react-modal'
import Select from 'react-select'

import {
  TextInput, Button, Textarea,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { func, number } from 'prop-types'

import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import useSentry from 'Pods/Shared/Hooks/useSentry'

import ReportModalHeader from './Components/ReportModalHeader/ReportModalHeader'

const reasons = [
  {
    label: 'Adult or violent content',
    value: 'adult',
  },
  {
    label: 'Copyright infringement',
    value: 'copyright',
  },
  {
    label: 'AI-generated content',
    value: 'ai_generated',
  },
]

function ReportModal({ closeModal, itemId }) {
  const [reason, setReason] = useState('')
  const [originalContentLink, setOriginalContentLink] = useState('')
  const [comment, setComment] = useState('')
  const [showErrors, setShowErrors] = useState(false)
  const [validationErrors, setValidationErrors] = useState({})

  const { postData, isLoading } = useApiPost()
  const { captureException } = useSentry()

  const validateForm = useCallback(() => {
    const errors = {}

    if (!reason || !reason.value) {
      errors.reason = 'Reason is required'
    }

    if (!comment || comment.trim() === '') {
      errors.comment = 'Comment is required'
    }

    if (reason?.value === 'copyright' && (!originalContentLink || originalContentLink.trim() === '')) {
      errors.originalContentLink = 'Original Content Link is required'
    }

    return errors
  }, [reason, comment, originalContentLink])

  const handleReasonChange = useCallback((value) => {
    setReason(value)
    if (value?.value !== 'copyright') {
      setOriginalContentLink('')
    }
  }, [])

  const handleOriginalContentLinkChange = useCallback((event) => {
    setOriginalContentLink(event.target.value)
  }, [])

  const handleCommentChange = useCallback((event) => {
    setComment(event.target.value)
  }, [])

  const handleSubmitReport = useCallback(() => {
    const errors = validateForm()
    setValidationErrors(errors)
    setShowErrors(true)

    if (Object.keys(errors).length > 0) {
      return
    }

    postData({
      path: '/api/internal/content_reports',
      data: {
        report: {
          category: reason.value,
          original_content_link: originalContentLink,
          report: comment,
          content_type: 'Item',
          content_id: itemId,
        },
      },
      onSuccess: (response) => {
        if (response.data.success) {
          window.showNotification(response.data.message, { type: 'success' })
          setReason('')
          setOriginalContentLink('')
          setComment('')
          setShowErrors(false)
          setValidationErrors({})
          closeModal()
        } else {
          const errorMessage = response.data.message || 'Something went wrong, please try again'
          window.showNotification(errorMessage, { type: 'error' })
          captureException(new Error(`Report submission failed: ${errorMessage}`), {
            extra: {
              itemId,
              reason: reason?.value,
              responseData: response.data,
            },
          })
        }
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.errors?.join(', ')
          || error.response?.data?.message
          || 'Something went wrong, please try again'
        window.showNotification(errorMessage, { type: 'error' })
        captureException(error, {
          extra: {
            itemId,
            reason: reason?.value,
            errorMessage,
          },
        })
      },
    })
  }, [reason, originalContentLink, comment, itemId, validateForm, postData, closeModal, captureException])

  return (
    <Modal
      isOpen
      shouldCloseOnOverlayClick
      onRequestClose={closeModal}
      contentLabel="Report Modal"
      ariaHideApp={false}
      className={{
        base: '',
        afterOpen: 'overlay-modal--container_after-open overlay-modal-container--report',
        beforeClose: 'overlay-modal--container_before-close',
      }}
      overlayClassName={{
        base: 'overlay-modal--background',
        afterOpen: 'overlay-modal--background_after-open',
        beforeClose: 'overlay-modal--background_before-close',
      }}
    >
      <div className="report-modal">
        <ReportModalHeader closeModal={closeModal} />
        <div className="report-modal__content">
          <div className="report-modal__select-container">
            <span className="report-modal__select-label">Reason</span>
            <Select
              className="report-modal__select"
              id="report-model"
              placeholder="Select reason"
              options={reasons}
              onChange={handleReasonChange}
              value={reason}
              searchable={false}
              clearable={false}
              defaultMenuIsOpen
            />
            {showErrors && validationErrors.reason && (
              <span className="report-modal__error-message">{validationErrors.reason}</span>
            )}
          </div>
          {reason?.value === 'copyright' && (
            <TextInput
              id="original-content-link"
              label="Original content link"
              type="primary"
              placeholder="Insert link here"
              onChange={handleOriginalContentLinkChange}
              value={originalContentLink}
              textInputClassName="report-modal__input"
              isWarning={showErrors && validationErrors.originalContentLink}
              helpText={showErrors && validationErrors.originalContentLink ? validationErrors.originalContentLink : ''}
              isPilot
            />
          )}
          <Textarea
            id="comment"
            label="Comment"
            placeholder="Type here"
            onChange={handleCommentChange}
            value={comment}
            textareaClassName="report-modal__textarea"
            rows={2}
            isWarning={showErrors && validationErrors.comment}
            helpText={showErrors && validationErrors.comment ? validationErrors.comment : ''}
            isPilot
          />
        </div>
        <div className="report-modal__footer">
          <Button
            type="secondary"
            customClass="report-modal__button"
            onClick={closeModal}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            customClass="report-modal__button"
            onClick={handleSubmitReport}
            disabled={isLoading}
          >
            {isLoading ? 'Submitting...' : 'Report'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

ReportModal.propTypes = {
  closeModal: func.isRequired,
  itemId: number.isRequired,
}

export default ReportModal
